import type { SignInStaffResponse } from "@wallyo/contracts/console";
import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { signInStaff, verifyStaffCode } from "../api/console-api";
import { useApi } from "../api/context";
import { createIntent } from "../api/idempotency";
import { CONSOLE_LANGUAGES, directionOf, LANGUAGE_NAMES, t } from "../i18n";
import { chooseLanguage, useLanguage } from "../i18n/session-language";
import { BrandMark } from "../shell/BrandMark";
import { ErrorNotice } from "../shell/ErrorNotice";
import { formatRemaining, useRemaining } from "./countdown";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { SET_PASSWORD_HASH } from "./set-password";

type Step = { kind: "credentials" } | { kind: "code"; challenge: SignInStaffResponse };

/**
 * Email and password, then the code sent by SMS. The session token only exists
 * once the code is verified; storing it switches the app to the shell.
 */
export function SignInFlow({ notice, info }: { notice?: unknown; info?: string | null }) {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<Step>({ kind: "credentials" });

  return (
    <AuthLayout>
      {step.kind === "credentials" ? (
        <CredentialsForm
          email={email}
          onEmailChange={setEmail}
          notice={notice}
          info={info}
          onChallenge={(challenge) => setStep({ kind: "code", challenge })}
        />
      ) : (
        <CodeForm challenge={step.challenge} onRestart={() => setStep({ kind: "credentials" })} />
      )}
    </AuthLayout>
  );
}

/**
 * The split (WALLIYO-0137): the mark and the languages down one green rail, the
 * work on the white.
 *
 * Nothing on this page sells anything. Whoever loads it already works here —
 * there is no public route to it — so the marketing line, the "Sign in" label
 * and the step counter all went, and the rail carries the two things a person
 * standing in front of it can actually use: who this is, and their language.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  const language = useLanguage();

  return (
    <div className="auth">
      <aside className="auth-rail">
        <div className="auth-lockup">
          <BrandMark size={48} tone="onDark" />
          <span>
            <span className="auth-word">{t("brand.name")}</span>
            <span className="auth-kicker">{t("brand.console")}</span>
          </span>
        </div>
        <span className="auth-rule" aria-hidden="true" />
        <span className="auth-gap" />
        {/* Eight languages, each in its own script: nobody hunts a menu for
            their own, and no label is needed above a list that names itself. */}
        <nav className="auth-langs" aria-label={t("header.language")}>
          {CONSOLE_LANGUAGES.map((each) =>
            each === language ? (
              <span key={each} className="auth-lang on" lang={each} dir={directionOf(each)} aria-current="true">
                {LANGUAGE_NAMES[each]}
              </span>
            ) : (
              <button
                key={each}
                type="button"
                className="auth-lang"
                lang={each}
                dir={directionOf(each)}
                onClick={() => chooseLanguage(each)}
              >
                {LANGUAGE_NAMES[each]}
              </button>
            ),
          )}
        </nav>
      </aside>
      <main className="auth-main">
        <div className="auth-card">{children}</div>
        {/* The only thing this page can honestly state before anyone signs in
            is what machine they are standing in front of. */}
        <p className="auth-foot">
          <span>
            <b>{t("signIn.environment")}</b>
            <code>{import.meta.env.MODE}</code>
          </span>
          <span>
            <b>{t("signIn.build")}</b>
            <code>{__APP_VERSION__}</code>
          </span>
        </p>
      </main>
    </div>
  );
}

function CredentialsForm(props: {
  email: string;
  onEmailChange: (email: string) => void;
  notice?: unknown;
  info?: string | null;
  onChallenge: (challenge: SignInStaffResponse) => void;
}) {
  const { client } = useApi();
  const intent = useRef(createIntent());
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const challenge = await signInStaff(
        client,
        { email: props.email.trim(), password },
        { idempotencyKey: intent.current.key() },
      );
      intent.current.reset();
      props.onChallenge(challenge);
    } catch (failure) {
      setError(failure);
      setPending(false);
    }
  }

  // A changed email or password is a new intent, so it gets a new key.
  const edit = (apply: () => void) => {
    intent.current.reset();
    apply();
  };

  return (
    <>
      <h1 className="auth-title">{t("signIn.heading")}</h1>
      {props.info ? (
        <p className="banner ok" role="status">
          {props.info}
        </p>
      ) : null}
      {props.notice != null && !error ? <ErrorNotice error={props.notice} /> : null}
      <form onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="email">{t("signIn.email")}</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            autoFocus
            value={props.email}
            onChange={(event) => edit(() => props.onEmailChange(event.target.value))}
          />
        </div>
        <div className="field">
          {/* The only way back in when a password is gone is the same page that
              sets one: it asks for the code by SMS, so it is the reset flow. */}
          <div className="field-head">
            <label htmlFor="password">{t("signIn.password")}</label>
            <a className="linkish" href={SET_PASSWORD_HASH}>
              {t("signIn.forgot")}
            </a>
          </div>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => edit(() => setPassword(event.target.value))}
          />
        </div>
        {error ? <ErrorNotice error={error} context="signIn" /> : null}
        <button className="btn primary block" type="submit" disabled={pending || !props.email || !password}>
          {pending ? t("signIn.submitting") : t("signIn.submit")}
        </button>
      </form>
      {/* Google Workspace is how everyone signs in (WALLIYO-0120); the password
          above is the break-glass path for an account that has to get in when
          Google cannot be reached. */}
      <GoogleSignInButton />
    </>
  );
}

function CodeForm({ challenge, onRestart }: { challenge: SignInStaffResponse; onRestart: () => void }) {
  const { client, session } = useApi();
  const intent = useRef(createIntent());
  const [code, setCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const left = useRemaining(challenge.expiresAt);
  const expired = left === 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const verified = await verifyStaffCode(
        client,
        { challengeId: challenge.challengeId, code: code.trim() },
        { idempotencyKey: intent.current.key() },
      );
      session.set(verified.sessionToken);
    } catch (failure) {
      setError(failure);
      setPending(false);
    }
  }

  return (
    <>
      <div>
        <h1>{t("verify.title")}</h1>
        <p className="sub">{t("verify.subtitle", { phone: challenge.maskedPhone })}</p>
      </div>
      <form onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="code">{t("verify.code")}</label>
          <input
            id="code"
            className="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            required
            autoFocus
            disabled={expired}
            value={code}
            onChange={(event) => {
              intent.current.reset();
              setCode(event.target.value.replace(/\D/g, ""));
            }}
          />
        </div>
        <p className="foot countdown" aria-live="polite">
          {expired ? t("verify.expired") : t("verify.expiresIn", { time: formatRemaining(left) })}
        </p>
        {error ? <ErrorNotice error={error} context="verify" /> : null}
        <button className="btn primary" type="submit" disabled={pending || expired || code.length === 0}>
          {pending ? t("verify.submitting") : t("verify.submit")}
        </button>
        <button className="linkish" type="button" onClick={onRestart}>
          {t("verify.restart")}
        </button>
      </form>
    </>
  );
}
